// Fixed-timestep loop (TECH_SPEC §4.1): the sim always advances in whole 60 Hz
// steps; rendering interpolates between the last two states. Slow-mo scales
// wall-clock time only — the sim never sees a different timestep.

export const FPS = 60;
export const STEP_MS = 1000 / FPS;

const MAX_FRAME_MS = 250; // tab-switch / breakpoint guard
const MAX_STEPS_PER_FRAME = 5; // no spiral of death

export interface LoopHandlers {
  /** Advance the simulation exactly one fixed step. */
  update(): void;
  /** alpha ∈ [0,1) — fraction of a step elapsed since the last update. */
  render(alpha: number): void;
}

export class GameLoop {
  private raf = 0;
  private last = 0;
  private acc = 0;
  private running = false;
  private paused = false;
  private timescale = 1;
  private slowFramesLeft = 0;
  private stepMultiplier = 1;

  constructor(private readonly handlers: LoopHandlers) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.paused = false;
    this.last = performance.now();
    this.acc = 0;
    this.raf = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  /** Freezes the sim but keeps rendering (pause overlay, revive offer). */
  pause(): void {
    this.paused = true;
    this.acc = 0;
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.last = performance.now();
    this.acc = 0;
  }

  get isRunning(): boolean {
    return this.running;
  }

  get isPaused(): boolean {
    return this.paused;
  }

  /** Playtest `&timescale=` (docs/DEV_PLAYTEST.md): N whole sim steps per real
   *  frame. The timestep itself never changes — only how many of them a single
   *  rAF is allowed to consume — so the run stays bit-identical. */
  setStepMultiplier(n: number): void {
    this.stepMultiplier = Math.max(1, Math.min(16, Math.floor(n)));
  }

  /** Near-miss juice: 0.5× for 20 sim frames (design §2.5). Frames max()-merge
   *  and the DEEPER slow-mo wins on scale: an overtake's 0.4× (§4bis.22 beat
   *  hierarchy) must not be overwritten back to 0.5× by a near miss landing
   *  inside its window. `timescale` is 1 whenever no window is active, so the
   *  min() is a no-op for a fresh slow-mo. */
  slowMo(frames: number, scale = 0.5): void {
    this.slowFramesLeft = Math.max(this.slowFramesLeft, frames);
    this.timescale = Math.min(this.timescale, scale);
  }

  /**
   * Cancel any slow-mo window immediately (§4bis.30). The duel's mid-run seal
   * slows time for 42 frames; a death inside that window must restore real time
   * BEFORE the death flash arms, or the §4bis.23 explanation would play out
   * against a stretched clock next to a trophy for the run that just ended.
   * The death flash has absolute precedence — this is how it takes it.
   */
  clearSlowMo(): void {
    this.slowFramesLeft = 0;
    this.timescale = 1;
  }

  get slowMoActive(): boolean {
    return this.slowFramesLeft > 0;
  }

  /** Test probe: the wall-clock scale currently applied (render-side only). */
  get slowMoScale(): number {
    return this.timescale;
  }

  private readonly tick = (now: number): void => {
    if (!this.running) return;
    this.raf = requestAnimationFrame(this.tick);

    const rawDelta = Math.min(now - this.last, MAX_FRAME_MS);
    this.last = now;

    if (!this.paused) {
      const maxSteps = MAX_STEPS_PER_FRAME * this.stepMultiplier;
      this.acc += rawDelta * this.timescale * this.stepMultiplier;
      let steps = 0;
      while (this.acc >= STEP_MS && steps < maxSteps && this.running && !this.paused) {
        this.acc -= STEP_MS;
        steps++;
        this.handlers.update();
        if (this.slowFramesLeft > 0) {
          this.slowFramesLeft--;
          if (this.slowFramesLeft === 0) this.timescale = 1;
        }
      }
      if (this.acc > STEP_MS * maxSteps) this.acc = 0;
    }

    this.handlers.render(this.paused ? 0 : Math.min(1, this.acc / STEP_MS));
  };
}
